import React, { ChangeEvent, Fragment, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PartCard from '../PartCard/PartCard';
import styles from './DashboardCanvas.module.scss';
import GroupStateService from '../../services/GroupStateService';
import PartHighlightService from '../../services/PartHighlightService';
import { GroupUnion, PartUnion } from '../../models/GroupUnion';
import SystemService from '../../services/SystemService';
import {
  allManifoldLinkedPartsAreConfigured,
  getIncompleteLinkedPartsToSegment,
  currentManifoldWorkflowStep,
  getNonLinkedPartsToSegment,
} from './ManifoldCanvasUtil';
import OutsideClickHandler from 'react-outside-click-handler';
import { useTranslation } from 'react-i18next';
import { GetSystemResponse, UnitDefinition, Part, Group, LinkState } from '../../models/Api';
import { TextField } from '@mui/material';
import { WorkflowSteps } from '../../models/WorkflowSteps';
import DialogBox from '../DialogBox/DialogBox';
import {
  getUniqueName,
  findWorkflowStep,
  deleteGroupConfirmDialog,
  deletePartConfirmDialog,
  confirmManifoldRevert,
  createGroup,
  createPart,
  isPartDeletable,
  isGroupLastInLine,
  getManifoldSegmentGroupId,
  manifoldStationDeleteAlert,
  isManifoldOptimized,
} from '../../utils/Utils';
import { AddPartModal } from '../../components/AddPartModal/AddPartModal';
import { AddPartMode } from '../../models/AddPartMode';
import SizingToolService, { SizingToolState } from '../../services/SizingToolService';
import SpinnerService from '../../services/SpinnerService';

export function DashboardCanvas(props: {
  system: GetSystemResponse;
  unitDefinitions: UnitDefinition[];
  handleLaunchSizingTool: (system: GetSystemResponse | undefined, part: Part) => void;
  isSizingTabOpen: () => boolean;
  openSizingAlertDialog: () => void;
}) {
  const { t } = useTranslation();
  const { system, unitDefinitions, isSizingTabOpen, openSizingAlertDialog } = props;
  const [Groups, setGroups] = useState<GroupUnion[]>([] as GroupUnion[]);
  const [showGroupMenuItems, setShowGroupMenuItems] = useState(-1);
  const [selectedPartId, setSelectedPartId] = useState('');
  const [partNameEdit, setPartNameEdit] = useState('');
  const [partName, setPartName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [manifoldActionName, setManifoldActionName] = useState('');
  const [stateChanged, setStateChanged] = useState(false);
  const [partToDelete, setPartToDelete] = useState({} as Part);
  const [activeGroup, setActiveGroup] = useState({ group: {} as GroupUnion, editState: false });
  const [showDeletePartDialog, setShowDeletePartDialog] = useState(false);
  const [showPartSizingValidation, setShowPartSizingValidation] = useState(false);
  const [showSelectedPart, setShowSelectedPart] = useState({} as Part);
  const [deleteGroupDialog, setDeleteGroupDialog] = useState({
    show: false,
    withApplicationGroups: false,
  });
  const [showSubsequentPartModal, setShowSubsequentPartModal] = useState(false);
  const [showFirstPartModal, setShowFirstPartModal] = useState(false);
  const [selectedTile, setSelectedTile] = useState({
    previousPart: {} as Part,
    currentGroup: {} as Group,
  });
  const [showPartDeleteAlertDialog, setShowPartDeleteAlertDialog] = useState(false);
  const [showGroupDeleteAlertDialog, setShowGroupDeleteAlertDialog] = useState(false);
  const [validateConfigureParts, setValidateConfigureParts] = useState({
    incompleteParts: '',
    showManifoldValidationAlert: false,
  });
  const manifoldGroupDefinitionId = props.system?.groupDefinitions?.find((definition) => definition.uniqueName === 'PneumaticSupplyManifolds')?.id;
  const manifoldGroupId = props.system?.groups?.find((group) => group.definitionId === manifoldGroupDefinitionId)?.id as number;
  const [showOptimizedManifoldAlert, setShowOptimizedManifoldAlert] = useState(false);
  const [revertOptimizedManifold, setRevertOptimizedManifold] = useState(false);
  const [showDeleteManifoldValidation, setShowDeleteManifoldValidation] = useState(false);
  const [sizingToolState, setSizingToolState] = useState<SizingToolState>({
    part: {} as Part,
    showMiniSizing: false,
    createPartSizingData: false,
    sizingUnderway: false,
  });

  const linkStateMap = new Map();
  linkStateMap.set(LinkState.Good, 'good-connecting-link');
  linkStateMap.set(LinkState.Initial, 'initial-connecting-link');
  linkStateMap.set(LinkState.Incomplete, 'incomplete-connecting-link');

  function handleRequestToOpenSizing(part: Part) {
    if (!sizingToolState.sizingUnderway) {
      const currentSystem = SystemService.getCurrentState().system as GetSystemResponse;
      props.handleLaunchSizingTool(currentSystem, part);
    }
  }

  useEffect(() => {
    GroupStateService.getState(setGroups);
    SizingToolService.getState(setSizingToolState);
  }, []);
  useEffect(() => {
    if (selectedPartId !== '') PartHighlightService.highlightPart(selectedPartId);
  }, [selectedPartId]);
  useEffect(() => {
    if (revertOptimizedManifold && manifoldActionName === 'delete') {
      deleteGroupConfirmDialog(
        activeGroup.group.instance,
        deleteGroupDialog.show,
        setDeleteGroupDialog,
        deleteGroupDialog.withApplicationGroups,
        setShowOptimizedManifoldAlert,
        revertOptimizedManifold,
        setManifoldActionName,
        setShowDeleteManifoldValidation,
      );
    }
    if (revertOptimizedManifold && manifoldActionName === 'disconnect') {
      const overlapGroupLink = system.overlapGroupLinks?.find((overlapGroup) => overlapGroup.overlappingGroupId === activeGroup.group.instance.id);
      if (overlapGroupLink && overlapGroupLink.linkedPartId !== null)
        SystemService.disconnectFromManifold(overlapGroupLink, setShowOptimizedManifoldAlert, setManifoldActionName);
    }
    if (revertOptimizedManifold && manifoldActionName === 'size') {
      const manifoldGroup = activeGroup.group;
      const manifoldPart = manifoldGroup.parts.length > 0 ? manifoldGroup.parts[0].instance : null;
      if (!manifoldPart) return;
      handleRequestToOpenSizing(manifoldPart);
    }
  }, [revertOptimizedManifold]);

  const handleShowPartCard = (id: string) => {
    if (selectedPartId === id) {
      setSelectedPartId('');
    } else {
      setSelectedPartId(id);
    }
  };

  const handleShowGroupMenuItems = (id: number) => {
    if (showGroupMenuItems === id) {
      setShowGroupMenuItems(-1);
    } else {
      setShowGroupMenuItems(id);
    }
  };

  const handlePartDelete = () => {
    const deletePart = system.parts?.find((part) => part.id === selectedPartId) as Part;
    setPartToDelete(deletePart);
    if (isPartDeletable(system, deletePart)) setShowDeletePartDialog(true);
    else setShowPartDeleteAlertDialog(true);
  };

  const handleGroupAction = (groupId: number) => {
    const groupUnion = Groups.find((group) => group.instance.id === groupId) as GroupUnion;
    setActiveGroup({ group: groupUnion, editState: false });
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, part?: PartUnion, group?: GroupUnion) => {
    if (e.target.id === 'partName') {
      setStateChanged(e.target.value !== part?.instance.name);
      setPartName(e.target.value);
    }
    if (e.target.id === 'groupName') {
      setStateChanged(e.target.value !== group?.instance.name);
      setGroupName(e.target.value);
    }
  };

  async function handleOnBlur(partId: string | undefined, groupId: number | undefined) {
    if ((partId && partName === '') || (groupId && groupName === '')) return;
    if (stateChanged) {
      if (partId) {
        SystemService.updatePartName(partId, getUniqueName(system, partName, false, true));
      } else if (groupId) {
        SystemService.updateGroupName(groupId, getUniqueName(system, groupName, true, true));
      }
      setStateChanged(false);
    }
  }

  const renderPartTileIcons = (part: PartUnion) => {
    const showBothIcons = part.instance.dataItems?.some((dataItem) => dataItem.key === 'PartId') && part.instance.isInactive;
    return (
      <div className={styles[`${showBothIcons ? 'icons-div' : 'check-icon-div'}`]}>
        {part.instance.dataItems?.some((dataItem) => dataItem.key === 'PartId') && <img src="images/checkicon.svg" className={styles['check-icon']}></img>}

        {part.instance.isInactive && <FontAwesomeIcon className={styles['exclamation-icon']} icon="circle-exclamation" />}
      </div>
    );
  };
  const renderPartTile = (part: PartUnion, isLastTile: boolean, isMasterPart?: boolean, isDualValve?: boolean) => {
    const partSilhouette = part.category?.options?.find((option) => option.addOnDashboard)?.silhouetteUrl;
    const baseName = isDualValve ? 'dual-valve-' : `${isLastTile ? 'last-' : ''}`;

    return (
      <>
        <div key={part.instance.id} className={styles[`${part.instance.isParkerPart ? `${baseName}part-tile` : `${baseName}part-tile-rounded`}`]}>
          {partNameEdit !== part.instance.id ? (
            <p title={part.instance.name as string} onClick={() => setPartNameEdit(part.instance.id as string)}>
              {part.instance.name}
            </p>
          ) : (
            partNameEdit === part.instance.id && (
              <TextField
                id="partName"
                color="secondary"
                className={styles['part-name-field']}
                autoFocus={true}
                onBlur={() => {
                  setPartNameEdit('');
                  handleOnBlur(part.instance.id, undefined);
                }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event, part, undefined)}
                inputProps={{ maxLength: 240 }}
                defaultValue={part.instance.name as string}
              />
            )
          )}
          {!isMasterPart && !isDualValve && renderPartTileIcons(part)}

          {isDualValve && <div className={styles['dual-valve-icons-div']}>{renderPartTileIcons(part)}</div>}

          <div
            className={styles['part-image-div']}
            onClick={(e) => {
              if (
                e.shiftKey ||
                (part.instance.isParkerPart && findWorkflowStep(part.instance) > WorkflowSteps.Size) ||
                (!part.instance.isParkerPart &&
                  part.instance.dataItems?.some((dataItem) => part.instance.isParkerPart === false && dataItem.key === 'SummaryReportData'))
              ) {
                handleShowPartCard(part.instance.id as string);
                handleGroupAction(part.instance.groupInstanceId as number);
              } else {
                PartHighlightService.highlightPart(part.instance.id as string);
                if (!isSizingTabOpen()) {
                  if (part.instance.isParkerPart) {
                    if (system.partLinks?.some((x) => x.endId === part.instance.id)) {
                      const linkedPartId = system.partLinks?.find((partlink) => partlink.endId === part.instance.id)?.startId;
                      const linkedPart = system.parts?.find((linkedParts) => linkedParts.id === linkedPartId) as Part;
                      const linkedIsPartConfigured = linkedPart.isParkerPart
                        ? linkedPart.dataItems?.find((dataItem) => dataItem.key === 'PartId')?.value
                        : linkedPart.dataItems?.find((dataItem) => dataItem.key === 'SummaryReportData')?.value;
                      if (!linkedIsPartConfigured) {
                        setShowSelectedPart(part.instance);
                        setShowPartSizingValidation(true);
                        return;
                      }
                    }
                    const segmentGroupId = getManifoldSegmentGroupId(part, system);
                    if (segmentGroupId) {
                      const nonLinkedParts = getNonLinkedPartsToSegment(segmentGroupId);
                      if (nonLinkedParts.length !== 0) {
                        setValidateConfigureParts({
                          incompleteParts: nonLinkedParts.toString().replaceAll(',', ', '),
                          showManifoldValidationAlert: true,
                        });
                      } else {
                        const incompleteParts = getIncompleteLinkedPartsToSegment(segmentGroupId);
                        if (incompleteParts.length === 0) {
                          handleRequestToOpenSizing(part.instance);
                        } else {
                          setValidateConfigureParts({
                            incompleteParts: incompleteParts.toString().replaceAll(',', ', '),
                            showManifoldValidationAlert: true,
                          });
                        }
                      }
                    } else {
                      handleRequestToOpenSizing(part.instance);
                    }
                  } else {
                    SizingToolService.setSizingToolState(part.instance, true, true, false);
                    SpinnerService.updateSpinnerStatus(0, false);
                  }
                } else {
                  openSizingAlertDialog();
                }
              }
            }}
          >
            {part.instance.thumbnail ? (
              <img
                className={styles['part-tile-thumbnail']}
                src={part.instance.thumbnail.substring(0, 2) === '//' ? `https:${part.instance.thumbnail}` : part.instance.thumbnail}
              />
            ) : (
              <img
                className={
                  styles[
                    part.instance.dataItems?.some((dataItem) => part.instance.isParkerPart === false && dataItem.key === 'SummaryReportData')
                      ? 'part-tile-silhouette-dark'
                      : 'part-tile-silhouette'
                  ]
                }
                src={`images/silhouette/${partSilhouette ? partSilhouette : 'parkerdefault.svg'}`}
              />
            )}
          </div>
          <p className={styles['part-id']}>{part.instance.dataItems?.find((dataItem) => dataItem.key === 'PartId')?.value}</p>
        </div>
        <OutsideClickHandler
          onOutsideClick={() => {
            if (selectedPartId === part.instance.id) {
              setSelectedPartId('');
            }
          }}
        >
          <div>
            {selectedPartId === part.instance.id && (
              <PartCard
                part={part}
                unitDefinitions={unitDefinitions}
                system={system}
                selectedPartId={selectedPartId}
                handlePartDelete={handlePartDelete}
                handleLaunchSizingTool={handleRequestToOpenSizing}
                isSizingTabOpen={isSizingTabOpen}
                openSizingAlertDialog={openSizingAlertDialog}
              />
            )}
          </div>
        </OutsideClickHandler>
      </>
    );
  };

  const getManifoldWorkflowStepMenuItem = (manifoldGroup: GroupUnion) => {
    const workflowStep = currentManifoldWorkflowStep(manifoldGroup);
    const manifoldPart = manifoldGroup.parts.length > 0 ? manifoldGroup.parts[0].instance : null;
    const segmentGroup = Groups.find((group) => group.instance.parentGroupId === manifoldGroup.instance.id) as GroupUnion;
    if (!segmentGroup) return;
    const masterPart = manifoldPart || segmentGroup.parts[0].instance;
    if (workflowStep >= WorkflowSteps.Size)
      return (
        <li
          onClick={() => {
            if (isSizingTabOpen()) {
              openSizingAlertDialog();
            } else {
              if (isManifoldOptimized(manifoldGroupId)) {
                setManifoldActionName('size');
                setShowOptimizedManifoldAlert(true);
                return;
              }
              handleRequestToOpenSizing(masterPart);
            }
          }}
        >
          <a>{t('ViewEditSizing')}</a>
        </li>
      );
    else if (workflowStep === WorkflowSteps.Add && allManifoldLinkedPartsAreConfigured(manifoldGroup))
      return (
        <li
          onClick={() => {
            if (isSizingTabOpen()) {
              openSizingAlertDialog();
            } else {
              handleRequestToOpenSizing(masterPart);
            }
          }}
        >
          <a>{t('HelpMeFindTheRightPart')}</a>
        </li>
      );
  };

  function manifoldGroupMenuItems(group: GroupUnion) {
    return (
      <>
        <ul className={showGroupMenuItems === group.instance.id ? styles['group-tile-menu-dropdown-open'] : styles['group-tile-menu-dropdown-close']}>
          {getManifoldWorkflowStepMenuItem(group)}
          <li
            onClick={() => {
              setDeleteGroupDialog({ show: true, withApplicationGroups: false });
            }}
          >
            <a>{t('DeleteManifoldOnly')}</a>
          </li>
          <li
            onClick={() => {
              setDeleteGroupDialog({ show: true, withApplicationGroups: true });
            }}
          >
            <a>{t('DeleteManifoldAndApplicationGroups')}</a>
          </li>
        </ul>
      </>
    );
  }

  function groupMenuItems(group: GroupUnion) {
    const linkedToManifold = system.virtualGroups?.some((virtualGroup) => virtualGroup.baseGroupId === group.instance.id);

    return (
      <>
        <ul className={showGroupMenuItems === group.instance.id ? styles['group-tile-menu-dropdown-open'] : styles['group-tile-menu-dropdown-close']}>
          <li
            onClick={async () => {
              const createGroupResponse = await createGroup(
                system.id as string,
                activeGroup?.group.instance.definitionId as number,
                getUniqueName(system, activeGroup?.group.instance.name as string, true, false),
                activeGroup.group.instance.isFinished,
                null,
              );

              if (createGroupResponse.status === 200) {
                const rows = system.parts?.map((part) => part.row) as number[];
                const highestRowValue = Math.max(...rows);
                system.parts?.map(async (part) => {
                  if (part.groupInstanceId === group.instance.id) {
                    createPart(
                      createGroupResponse.data.id as number,
                      activeGroup.group.instance.isFinished as boolean,
                      part.categoryId as number,
                      getUniqueName(system, part?.name as string, false, false),
                      highestRowValue + 1,
                      part?.column as number,
                      part?.order as number,
                    );
                  }
                });
              }
            }}
          >
            <a>{t('Duplicate')}</a>
          </li>
          <li
            onClick={() => {
              const isLastGroup = isGroupLastInLine(system, group.instance.id as number);
              if (isLastGroup) setDeleteGroupDialog({ show: true, withApplicationGroups: false });
              else setShowGroupDeleteAlertDialog(true);
            }}
          >
            <a>{t('Delete')}</a>
          </li>

          {system.templateDashboardType === 'Manifold' && linkedToManifold && (
            <li
              onClick={() => {
                const overlapGroupLink = system.overlapGroupLinks?.find((overlapGroup) => overlapGroup.overlappingGroupId === group.instance.id);
                if (overlapGroupLink && overlapGroupLink.linkedPartId !== null)
                  SystemService.disconnectFromManifold(overlapGroupLink, setShowOptimizedManifoldAlert, setManifoldActionName);
              }}
            >
              <a>{t('Disconnect')}</a>
            </li>
          )}
        </ul>
      </>
    );
  }

  const getConnectionLink = (group: Group, part: Part | undefined, isVirtualGroup: boolean) => {
    if (isVirtualGroup) {
      return system?.overlapGroupLinks?.find((overlapGroupLink) => overlapGroupLink.overlappingGroupId === group.id)?.linkState;
    } else {
      const partLinkState = system.partLinks?.find((partLinkObj) => partLinkObj.startId === part?.id)?.state;

      return partLinkState ? partLinkState : LinkState.Incomplete;
    }
  };

  const renderConnectionLink = (group: GroupUnion, part: PartUnion, isVirtualGroup: boolean) => {
    const linkState = getConnectionLink(group.instance, part.instance, isVirtualGroup);
    return (
      linkState && (
        <div className={styles[`${linkStateMap.get(linkState)}`]}>
          {linkState === 'Good' && <FontAwesomeIcon icon={['far', 'circle-check']} className={styles['check-icon-success']} />}
        </div>
      )
    );
  };

  const sortByColumn = (parts: PartUnion[]) => {
    return parts.sort((a, b) => (a.instance.column as number) - (b.instance.column as number));
  };

  const renderSubsequentPartTile = (part: PartUnion, group: GroupUnion) => {
    return (
      <div
        className={styles['add-part-tile']}
        onClick={() => {
          setSelectedTile({
            previousPart: part.instance,
            currentGroup: group.instance,
          });
          setShowSubsequentPartModal(true);
        }}
      >
        <img src="images/plusiconlight.png" />
      </div>
    );
  };

  const renderGroupTile = (baseGroup: GroupUnion, isVirtualGroup: boolean, additionalOverlappingGroup?: GroupUnion) => {
    if (!baseGroup)
      return (
        <div className={styles['group-tiles-wrapper']}>
          <div className={styles['placeholder-group-tile']}>{/* placeholder for intermediate modules/parts with no overlapping groups */}</div>
        </div>
      );
    return (
      <div key={baseGroup.instance.id} className={styles['group-tiles-wrapper']}>
        <div className={styles[`${isVirtualGroup ? 'virtual' + '-group-tile' : 'group-tile'}`]}>
          <OutsideClickHandler
            onOutsideClick={() => {
              if (showGroupMenuItems === baseGroup.instance.id) {
                setShowGroupMenuItems(-1);
              }
            }}
          >
            <div
              className={styles['group-tile-menu-icon']}
              onClick={() => {
                handleShowGroupMenuItems(baseGroup.instance.id as number);
                handleGroupAction(baseGroup.instance.id as number);
              }}
            >
              {baseGroup.definition.uniqueName !== 'PneumaticManifoldSegment' && <FontAwesomeIcon icon="bars" />}
              {groupMenuItems(baseGroup)}
            </div>
          </OutsideClickHandler>

          {!activeGroup.editState || activeGroup.group.instance.id !== baseGroup.instance.id ? (
            <p onClick={() => setActiveGroup({ group: baseGroup, editState: true })}>{baseGroup.instance.name}</p>
          ) : (
            activeGroup.editState &&
            activeGroup.group.instance.id === baseGroup.instance.id && (
              <TextField
                id="groupName"
                color="secondary"
                className={styles['group-name-field']}
                autoFocus={true}
                onBlur={() => {
                  setActiveGroup({ group: {} as GroupUnion, editState: false });
                  handleOnBlur(undefined, baseGroup.instance.id);
                }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event, undefined, baseGroup)}
                inputProps={{ maxLength: 240 }}
                defaultValue={baseGroup.instance.name as string}
              />
            )
          )}

          <div className={styles[`${isVirtualGroup ? 'virtual-group-part-tile-wrapper' : 'part-tile-wrapper'}`]}>
            {!isVirtualGroup && baseGroup.parts.length === 0 && (
              <div
                className={styles['add-part-tile']}
                onClick={() => {
                  setSelectedTile({
                    previousPart: {} as Part,
                    currentGroup: baseGroup.instance,
                  });
                  setShowFirstPartModal(true);
                }}
              >
                <img src="images/plusiconlight.png" />
              </div>
            )}

            {isVirtualGroup && baseGroup.parts.length === 0 ? (
              <>
                <div
                  className={styles['add-part-tile']}
                  onClick={() => {
                    setSelectedTile({
                      previousPart: {} as Part,
                      currentGroup: baseGroup.instance,
                    });
                    setShowFirstPartModal(true);
                  }}
                >
                  <img src="images/plusiconlight.png" />
                </div>
                <div className={styles[`${linkStateMap.get(getConnectionLink(baseGroup.instance, undefined, isVirtualGroup))}`]}></div>
              </>
            ) : (
              sortByColumn(baseGroup?.parts).map((part, i) => {
                const isLastPart = baseGroup.parts.length - 1 === i && (baseGroup.instance.isFinished as boolean);
                return (
                  <Fragment key={part.instance.id}>
                    {renderPartTile(part, isLastPart)}
                    {!isLastPart && renderConnectionLink(baseGroup, part, isVirtualGroup)}

                    {additionalOverlappingGroup && (
                      <>
                        <div className={styles['additional-overlapping-group-part-tile']}>{renderPartTile(additionalOverlappingGroup.parts[0], false)}</div>
                        <div className={styles['additional-overlapping-group-connecting-link']}>
                          {renderConnectionLink(additionalOverlappingGroup, additionalOverlappingGroup.parts[0], isVirtualGroup)}
                        </div>
                      </>
                    )}

                    {baseGroup.parts.length - 1 === i && !baseGroup.instance.isFinished && !isVirtualGroup && renderSubsequentPartTile(part, baseGroup)}
                  </Fragment>
                );
              })
            )}
          </div>
        </div>
        {baseGroup.instance.isFinished && !baseGroup.instance.isLastInLine && (
          <div className={styles['add-group-tile']}>
            <img src="images/plusiconlight.png" />
          </div>
        )}
      </div>
    );
  };

  const sortByOrder = (parts: PartUnion[]) => {
    return parts.sort((a, b) => (a.instance.order as number) - (b.instance.order as number));
  };

  const renderSegmentGroupTile = (group: GroupUnion, parentGroup: GroupUnion) => {
    return (
      <div key={group.instance.id} className={styles['group-tiles-wrapper']}>
        <div className={styles['segment-group-part-tile-wrapper']}>
          {parentGroup.instance.partAssemblyData && <div>{group.instance.name}</div>}
          {sortByOrder(group.parts).map((part, i) => {
            const linkedOverlapGroupLinks = system.overlapGroupLinks?.filter((overlapGroupLink) => overlapGroupLink.intersectedPartId === part.instance.id);

            const linkedGroupUnions = linkedOverlapGroupLinks?.map((overlapGroup) =>
              Groups.find((groupUnion) => groupUnion.instance.id === overlapGroup.overlappingGroupId),
            ) as GroupUnion[];

            const baseGroup = linkedGroupUnions.find((linkedGroup) =>
              system.virtualGroups?.some((virtualGroup) => virtualGroup.baseGroupId === linkedGroup?.instance.id),
            );
            const additionalOverlappingGroup = linkedGroupUnions.find((linkedGroup) => linkedGroup?.instance.id !== baseGroup?.instance.id);

            return (
              <div key={part.instance.id} className={styles['group-tile-wrapper']}>
                {linkedGroupUnions.length == 1
                  ? renderGroupTile(baseGroup as GroupUnion, true)
                  : renderGroupTile(baseGroup as GroupUnion, true, additionalOverlappingGroup)}
                <div className={styles['segment-group-part-tile']}>
                  {renderPartTile(part, group.parts.length - 1 === i && (group.instance.isFinished as boolean), undefined, linkedGroupUnions.length > 1)}
                  <div className={styles[`${linkedGroupUnions.length > 1 ? 'dual-valve-virtual-group-indices' : 'virtual-group-indices'}`]}>{i + 1}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderManifoldGroupTile = (group: GroupUnion) => {
    const segmentDefinitionId = system.groupDefinitions?.find((definitionId) => definitionId.uniqueName === 'PneumaticManifoldSegment')?.id;
    const segmentGroups = Groups.filter((groupObj) => groupObj.instance.definitionId === segmentDefinitionId);
    const masterPart = group.parts ? group.parts[0] : undefined;

    return (
      <div key={group.instance.id} className={styles['group-tiles-wrapper']}>
        <div className={styles['manifold-group-tile']}>
          <OutsideClickHandler
            onOutsideClick={() => {
              if (showGroupMenuItems === group.instance.id) {
                setShowGroupMenuItems(-1);
              }
            }}
          >
            <div
              className={styles['group-tile-menu-icon']}
              onClick={() => {
                handleShowGroupMenuItems(group.instance.id as number);
                handleGroupAction(group.instance.id as number);
              }}
            >
              {group.definition.uniqueName !== 'PneumaticManifoldSegment' && <FontAwesomeIcon icon="bars" />}

              {manifoldGroupMenuItems(group)}
            </div>
          </OutsideClickHandler>
          {!activeGroup.editState || activeGroup.group.instance.id !== group.instance.id ? (
            <p className={styles['manifold-group-name']} onClick={() => setActiveGroup({ group: group, editState: true })}>
              {group.instance.name}
            </p>
          ) : (
            activeGroup.editState &&
            activeGroup.group.instance.id === group.instance.id && (
              <TextField
                id="groupName"
                color="secondary"
                className={styles['group-name-field']}
                autoFocus={true}
                onBlur={() => {
                  setActiveGroup({ group: {} as GroupUnion, editState: false });
                  handleOnBlur(undefined, group.instance.id);
                }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event, undefined, group)}
                inputProps={{ maxLength: 240 }}
                defaultValue={group.instance.name as string}
              />
            )
          )}
          <div className={styles['manifold-seperation-line']}></div>
          {masterPart && (
            <>
              <div className={styles['segment-group-part-tile-wrapper']}>
                {renderPartTile(masterPart, true, true)}
                <div className={styles['master-part-icons-div']}>{renderPartTileIcons(masterPart)}</div>
              </div>
              <div className={styles['master-part-seperation-line']}></div>
            </>
          )}
          {segmentGroups.map((segmentGroup: GroupUnion, i) => (
            <div key={segmentGroup.instance.id}>
              {renderSegmentGroupTile(segmentGroup as GroupUnion, group as GroupUnion)}
              {i < segmentGroups.length - 1 && <div className={styles['segments-seperation-line']}></div>}
            </div>
          ))}
          {group.instance.partAssemblyData && (
            <>
              <div className={styles['end-cap-seperation-line']}></div>
              <div className={styles['end-cap']}>{t('EndCap')}</div>
            </>
          )}
        </div>
        {group.instance.isFinished && !group.instance.isLastInLine && (
          <div className={styles['add-group-tile']}>
            <img src="images/plusiconlight.png" />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <DialogBox
        title={showPartDeleteAlertDialog ? t('CannotDeletePart') : t('CannotDeleteGroup')}
        message={showPartDeleteAlertDialog ? t('OnlyTheLastPart...') : t('OnlyTheLastGroup...')}
        showModalState={showPartDeleteAlertDialog || showGroupDeleteAlertDialog}
        closeAction={() => {
          setShowPartDeleteAlertDialog(false);
          setShowGroupDeleteAlertDialog(false);
        }}
      />
      <DialogBox
        title={t('CannotSizeManifold')}
        message={t('PartsLinkedToValvesNotDone', { validateConfigureParts })}
        showModalState={validateConfigureParts.showManifoldValidationAlert}
        closeAction={() => {
          setValidateConfigureParts({
            incompleteParts: '',
            showManifoldValidationAlert: false,
          });
        }}
      />
      <DialogBox
        title={t('PartSizingValidation', { showSelectedPart })}
        message={t('PartSizingValidationMessage')}
        showModalState={showPartSizingValidation}
        closeAction={() => {
          setShowPartSizingValidation(false);
        }}
      />
      <AddPartModal
        system={system}
        modalState={showSubsequentPartModal}
        mode={AddPartMode.SubsequentPartForGroup}
        previousPart={selectedTile.previousPart}
        currentGroup={selectedTile.currentGroup}
        changeModalState={(show: boolean) => {
          setShowSubsequentPartModal(show);
        }}
      />

      <AddPartModal
        system={system}
        modalState={showFirstPartModal}
        mode={AddPartMode.FirstPartForGroup}
        currentGroup={selectedTile.currentGroup}
        changeModalState={(show: boolean) => setShowFirstPartModal(show)}
      />

      {activeGroup.group.instance &&
        !revertOptimizedManifold &&
        deleteGroupConfirmDialog(
          activeGroup.group.instance,
          deleteGroupDialog.show,
          setDeleteGroupDialog,
          deleteGroupDialog.withApplicationGroups,
          setShowOptimizedManifoldAlert,
          revertOptimizedManifold,
          setManifoldActionName,
          setShowDeleteManifoldValidation,
        )}
      {showOptimizedManifoldAlert &&
        confirmManifoldRevert(showOptimizedManifoldAlert, setShowOptimizedManifoldAlert, setRevertOptimizedManifold, manifoldGroupId)}
      {showDeleteManifoldValidation && manifoldStationDeleteAlert(showDeleteManifoldValidation, setShowDeleteManifoldValidation)}
      {partToDelete && deletePartConfirmDialog(partToDelete, showDeletePartDialog, setShowDeletePartDialog)}
      <div className={styles['dashboard-canvas']}>
        {Groups.map((group) => {
          if (group.definition.id === manifoldGroupDefinitionId) {
            return renderManifoldGroupTile(group);
          }
        })}
        {Groups.map((group) => {
          if (
            !system.overlapGroupLinks?.some((overlapGroup) => overlapGroup.overlappingGroupId === group.instance.id) &&
            group.definition.uniqueName !== 'PneumaticManifoldSegment' &&
            group.definition.id !== manifoldGroupDefinitionId
          ) {
            return renderGroupTile(group, false);
          }
        })}
      </div>
    </>
  );
}
